import { useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart3, TrendingUp, TrendingDown, PieChart,
  Ticket, Users, CheckCircle2, Phone, PhoneIncoming,
  PhoneOutgoing, PhoneMissed, FileText, AlertCircle,
} from 'lucide-react'
import { Card, PageHeader, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent, ErrorState } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { InventoryTurnoverSection } from '@/pages/artikel/sections/InventoryTurnoverSection.tsx'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  TopArtikelRohertragTile,
  MargenTrendTile,
  SchwachmargenAlarmTile,
} from '@/components/stats/index.ts'
import { useTicketStats, useCallStats, useInvoiceStats } from '@/hooks/stats'

type StatTab = 'overview' | 'lagerumschlag' | 'margen'

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export function StatistikenPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as StatTab | null
  const [activeTab, setActiveTab] = useState<StatTab>(
    tabParam === 'lagerumschlag' ? 'lagerumschlag'
    : tabParam === 'margen' ? 'margen'
    : 'overview',
  )
  const [margenTenantId, setMargenTenantId] = useState<string | null>(null)
  const { tenants } = useTenantStore()

  function handleTabChange(tab: StatTab) {
    setActiveTab(tab)
    setSearchParams(tab !== 'overview' ? { tab } : {}, { replace: true })
  }

  const tickets = useTicketStats()
  const calls = useCallStats()
  const invoices = useInvoiceStats()

  const t = tickets.data
  const c = calls.data
  const inv = invoices.data

  return (
    <div className="space-y-6">
      <PageHeader
        title="Statistiken"
        description="Übersicht über Tickets, Anrufe und Rechnungen"
      />

      <Tabs value={activeTab} onValueChange={(v: string) => handleTabChange(v as StatTab)}>
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="w-4 h-4" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="lagerumschlag">
            <TrendingUp className="w-4 h-4" />
            Lagerumschlag
          </TabsTrigger>
          <TabsTrigger value="margen">
            <PieChart className="w-4 h-4" />
            Margen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-6 mt-4">

            {/* Tickets */}
            <section className="space-y-3">
              <h2 className="text-h3 text-gray-700 flex items-center gap-2">
                <Ticket className="w-4 h-4" /> Tickets
              </h2>
              {t?.error && !t.tanssConnected && (
                <ErrorState
                  title="TANSS nicht erreichbar"
                  description={t.error}
                />
              )}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                  label="Offen gesamt"
                  value={t?.allOpen ?? '—'}
                  icon={<Ticket className="w-5 h-5 text-blue-600" />}
                  loading={tickets.isLoading}
                  trend={t?.allOpen && t.allOpen > 10 ? 'up' : 'neutral'}
                />
                <KpiCard
                  label="Nicht zugewiesen"
                  value={t?.unassigned ?? '—'}
                  icon={<Users className="w-5 h-5 text-orange-500" />}
                  loading={tickets.isLoading}
                  trend={t?.unassigned && t.unassigned > 0 ? 'up' : 'neutral'}
                  trendColor={t?.unassigned && t.unassigned > 0 ? 'red' : 'green'}
                />
                <KpiCard
                  label="Überfällig"
                  value={t?.overdue ?? '—'}
                  icon={<CheckCircle2 className="w-5 h-5 text-red-500" />}
                  loading={tickets.isLoading}
                  trend={t?.overdue && t.overdue > 0 ? 'up' : 'neutral'}
                  trendColor={t?.overdue && t.overdue > 0 ? 'red' : 'green'}
                />
                <KpiCard
                  label="Meine Tickets"
                  value={t?.own ?? '—'}
                  icon={<BarChart3 className="w-5 h-5 text-brand-600" />}
                  loading={tickets.isLoading}
                />
              </div>
              {t && t.allOpen > 0 && (
                <Card>
                  <Card.Body>
                    <div className="space-y-3">
                      <ProgressRow
                        label="Eigene Tickets"
                        value={t.own}
                        total={t.allOpen}
                        barClassName="bg-brand-500"
                      />
                      <ProgressRow
                        label="Nicht zugewiesen"
                        value={t.unassigned}
                        total={t.allOpen}
                        barClassName="bg-orange-400"
                      />
                      <ProgressRow
                        label="Überfällig"
                        value={t.overdue}
                        total={t.allOpen}
                        barClassName="bg-red-500"
                      />
                    </div>
                  </Card.Body>
                </Card>
              )}
            </section>

            {/* Anrufe */}
            <section className="space-y-3">
              <h2 className="text-h3 text-gray-700 flex items-center gap-2">
                <Phone className="w-4 h-4" /> Anrufe heute
              </h2>
              {c?.error && (
                <ErrorState
                  title="Anruf-Statistiken nicht verfügbar"
                  description={c.error}
                />
              )}
              {!c?.error && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <KpiCard
                    label="Gesamt"
                    value={c?.total ?? '—'}
                    icon={<Phone className="w-5 h-5 text-gray-500" />}
                    loading={calls.isLoading}
                  />
                  <KpiCard
                    label="Eingehend"
                    value={c?.incoming ?? '—'}
                    icon={<PhoneIncoming className="w-5 h-5 text-green-500" />}
                    loading={calls.isLoading}
                    trend={c?.incoming && c.incoming > 0 ? 'up' : 'neutral'}
                    trendColor="green"
                  />
                  <KpiCard
                    label="Ausgehend"
                    value={c?.outgoing ?? '—'}
                    icon={<PhoneOutgoing className="w-5 h-5 text-blue-500" />}
                    loading={calls.isLoading}
                  />
                  <KpiCard
                    label="Verpasst"
                    value={c?.missed ?? '—'}
                    icon={<PhoneMissed className="w-5 h-5 text-red-500" />}
                    loading={calls.isLoading}
                    trend={c?.missed && c.missed > 0 ? 'up' : 'neutral'}
                    trendColor={c?.missed && c.missed > 0 ? 'red' : 'green'}
                  />
                </div>
              )}
            </section>

            {/* Rechnungen */}
            <section className="space-y-3">
              <h2 className="text-h3 text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Rechnungen
              </h2>
              {inv?.error && (
                <ErrorState
                  title="Rechnungs-Statistiken nicht verfügbar"
                  description={inv.error}
                />
              )}
              {!inv?.error && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card>
                    <Card.Body className="p-5">
                      <p className="text-small text-gray-500 mb-1">Offen</p>
                      {invoices.isLoading ? (
                        <Skeleton className="h-8 w-24" />
                      ) : (
                        <>
                          <p className="text-h1 text-gray-900">{inv?.open.count ?? 0}</p>
                          <p className="text-caption text-gray-400 mt-1">
                            {inv ? formatCurrency(inv.open.totalGross) : '—'}
                          </p>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                  <Card>
                    <Card.Body className="p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-small text-gray-500">Überfällig</p>
                        {inv && inv.overdue.count > 0 && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {invoices.isLoading ? (
                        <Skeleton className="h-8 w-24" />
                      ) : (
                        <>
                          <p className={cn('text-h1', inv?.overdue.count ? 'text-red-600' : 'text-gray-900')}>
                            {inv?.overdue.count ?? 0}
                          </p>
                          <p className="text-caption text-gray-400 mt-1">
                            {inv ? formatCurrency(inv.overdue.totalGross) : '—'}
                          </p>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                  <Card>
                    <Card.Body className="p-5">
                      <p className="text-small text-gray-500 mb-1">Bezahlt</p>
                      {invoices.isLoading ? (
                        <Skeleton className="h-8 w-24" />
                      ) : (
                        <>
                          <p className="text-h1 text-green-600">{inv?.paid.count ?? 0}</p>
                          <p className="text-caption text-gray-400 mt-1">
                            {inv ? formatCurrency(inv.paid.totalGross) : '—'}
                          </p>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}
            </section>

          </div>
        </TabsContent>

        <TabsContent value="lagerumschlag">
          <div className="mt-4">
            <InventoryTurnoverSection />
          </div>
        </TabsContent>

        <TabsContent value="margen">
          <div className="mt-4 space-y-4">
            {tenants.length > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-small text-gray-500">Mandant:</span>
                <select
                  value={margenTenantId ?? ''}
                  onChange={(e) => setMargenTenantId(e.target.value || null)}
                  className="text-small border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-400"
                >
                  <option value="">Alle Mandanten</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TopArtikelRohertragTile tenantId={margenTenantId} />
              <SchwachmargenAlarmTile tenantId={margenTenantId} />
            </div>
            <MargenTrendTile tenantId={margenTenantId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

interface KpiCardProps {
  label: string
  value: string | number
  icon: ReactNode
  loading?: boolean
  trend?: 'up' | 'down' | 'neutral'
  trendColor?: 'red' | 'green' | 'gray'
}

function KpiCard({ label, value, icon, loading, trend = 'neutral', trendColor = 'gray' }: KpiCardProps) {
  return (
    <Card>
      <Card.Body className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-small text-gray-500">{label}</p>
          {trend === 'up' && (
            <TrendingUp className={cn('w-4 h-4', trendColor === 'red' ? 'text-red-400' : trendColor === 'green' ? 'text-green-400' : 'text-gray-400')} />
          )}
          {trend === 'down' && (
            <TrendingDown className={cn('w-4 h-4', trendColor === 'red' ? 'text-red-400' : trendColor === 'green' ? 'text-green-400' : 'text-gray-400')} />
          )}
        </div>
        <div className="flex items-end gap-2">
          <div className="p-2 rounded-md bg-gray-50">{icon}</div>
          {loading ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-h1 text-gray-900">{value}</p>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

function ProgressRow({ label, value, total, barClassName }: {
  label: string; value: number; total: number; barClassName: string
}) {
  const pct = Math.round((value / total) * 100)
  return (
    <div>
      <div className="flex justify-between text-small mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value} ({pct}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={cn('h-2 rounded-full transition-all', barClassName)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
